import type { Invoice, InvoiceLine, InvoiceTotals } from '../types/invoice'
import type { QuoteLine } from '../types/quote'

export function invoiceLineSubtotal(line: InvoiceLine): number {
  const quantity = Number.isFinite(line.quantity) ? line.quantity : 0
  const price = Number.isFinite(line.unitPrice) ? line.unitPrice : 0
  const discount = Number.isFinite(line.discount) ? line.discount : 0
  return quantity * price * (1 - discount / 100)
}

export function invoiceLineTotal(line: InvoiceLine): number {
  return invoiceLineSubtotal(line) * (1 + (line.vatRate || 0) / 100)
}

export function invoiceTotals(lines: InvoiceLine[]): InvoiceTotals {
  const subtotal = lines.reduce((sum, line) => sum + invoiceLineSubtotal(line), 0)
  const vatAmount = lines.reduce(
    (sum, line) => sum + invoiceLineSubtotal(line) * ((line.vatRate || 0) / 100),
    0,
  )
  return { subtotal, vatAmount, total: subtotal + vatAmount }
}

export function emptyInvoiceLine(): InvoiceLine {
  return {
    id: `iline-${Math.random().toString(36).slice(2, 9)}`,
    description: '',
    quantity: 1,
    unit: 'stk.',
    unitPrice: 0,
    vatRate: 25,
    discount: 0,
  }
}

/** Tilbudslinjer genbruges på fakturaen — alt forbliver redigerbart bagefter. */
export function invoiceLinesFromQuote(lines: QuoteLine[]): InvoiceLine[] {
  return lines.map((line) => ({
    id: `iline-${Math.random().toString(36).slice(2, 9)}`,
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unitPrice: line.unitPrice,
    vatRate: line.vatRate,
    discount: 0,
  }))
}

export function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Standard betalingsfrist: 8 dage netto. */
export function defaultDueDate(from: string, days = 8): string {
  const date = from ? new Date(from) : new Date()
  if (Number.isNaN(date.getTime())) return ''
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

/** En sendt faktura er forfalden, når forfaldsdatoen er overskredet. */
export function isOverdue(invoice: Invoice): boolean {
  if (invoice.status !== 'Sendt' || !invoice.dueDate) return false
  return invoice.dueDate < today()
}
